import React, { useState, useEffect } from "react";
import "./App.css";
import { 
  FaLinkedin, 
  FaEnvelope, 
  FaServer, 
  FaDatabase, 
  FaAws, 
  FaCode, 
  FaUsers,
  FaBookOpen
} from "react-icons/fa";
import { motion } from "framer-motion";

function App() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const skills = [
    { icon: <FaUsers />, name: "Engineering Leadership", description: "Leading efficient engineering teams through complex projects" },
    { icon: <FaServer />, name: "Distributed Systems", description: "Building resilient systems with concurrency & multithreading" },
    { icon: <FaDatabase />, name: "Data Infrastructure", description: "Kafka, Redis, MySQL, MongoDB, Cassandra, DynamoDB" },
    { icon: <FaCode />, name: "Microservices", description: "Designing and implementing scalable microservices architecture" },
    { icon: <FaAws />, name: "Cloud & DevOps", description: "AWS, Kubernetes, and modern deployment practices" }
  ];
  
  const blogs = [
    {
      title: "Mastering Operational Excellence: Preventing and Managing Outages Effectively",
      date: "Mar 20, 2025",
      excerpt: "Insights into building resilient systems, implementing effective monitoring, and establishing robust incident response processes to prevent and manage outages.",
      link: "https://medium.com/@Games24x7Tech/mastering-operational-excellence-preventing-and-managing-outages-effectively-17dc3e12de37",
      tags: ["Operational Excellence", "Incident Management", "System Reliability"]
    },
    {
      title: "How Games24x7 delivers millions of push notifications using AWS",
      date: "18 JAN 2024",
      excerpt: "A detailed look at how we transformed our push notification system using AWS Step Functions and Lambda to deliver over 30 million notifications in under 5 minutes.",
      link: "https://aws.amazon.com/blogs/gametech/how-games24x7-delivers-millions-of-push-notification-using-aws/",
      tags: ["AWS", "Serverless", "Push Notifications", "Step Functions"]
    },
    {
      title: "Standardizing and Automating Microservices Deployment",
      date: "Oct 17, 2022",
      excerpt: "How we streamlined our deployment process by standardizing and automating microservices deployment, leading to improved developer productivity and system reliability.",
      link: "https://medium.com/@Games24x7Tech/standardizing-and-automating-microservices-deployment-105b9fb5dabf",
      tags: ["Microservices", "DevOps", "Automation", "Deployment"]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
      {/* Header/Hero Section */}
      <motion.header 
        className="relative overflow-hidden h-screen flex items-center justify-center"
        initial="hidden"
        animate={isVisible ? "visible" : "hidden"}
        variants={fadeIn}
        transition={{ duration: 0.8 }}
      >
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20"></div>
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-purple-500"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.h1 
            className="text-5xl md:text-7xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Dhiraj Prajapati
          </motion.h1>
          
          <motion.p 
            className="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            Engineering leader building scalable, distributed and highly concurrent backend systems
          </motion.p>
          
          <motion.div 
            className="flex justify-center space-x-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <a href="mailto:dhirajpraj@gmail.com" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-medium flex items-center transition-all duration-300 transform hover:scale-105">
              <FaEnvelope className="mr-2" /> Get in touch
            </a>
            <a href="https://www.linkedin.com/in/dhiraj-prajapati-0482231b/" target="_blank" rel="noopener noreferrer" className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-full font-medium flex items-center transition-all duration-300 transform hover:scale-105">
              <FaLinkedin className="mr-2" /> LinkedIn
            </a>
          </motion.div>
        </div>
        
        <div className="absolute bottom-10 left-0 right-0 flex justify-center">
          <motion.div 
            className="animate-bounce"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            <svg className="w-6 h-6 text-gray-400" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </motion.div>
        </div>
      </motion.header>

      {/* About Section */}
      <motion.section 
        className="py-20 bg-gray-900"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        transition={{ duration: 0.8 }}
      >
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">About Me</span>
          </h2>
          
          <div className="max-w-3xl mx-auto bg-gray-800 p-8 rounded-2xl shadow-xl border border-gray-700">
            <p className="text-lg text-gray-300 leading-relaxed">
              I'm Dhiraj, a backend engineer by heart with a passion for building robust systems that can scale to millions of users. With extensive experience in distributed systems and high-performance computing, I specialize in designing architectures that can handle massive concurrency while maintaining reliability and data integrity.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mt-4">
              Throughout my career, I've led engineering teams to deliver complex projects, focusing on creating maintainable codebases that can evolve with changing business requirements. I enjoy tackling challenging technical problems and mentoring other engineers to grow their skills.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Skills Section */}
      <motion.section 
        className="py-20 bg-gray-800"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        transition={{ duration: 0.8 }}
      >
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">Skills & Expertise</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {skills.map((skill, index) => (
              <motion.div 
                key={index}
                className="bg-gray-700 rounded-xl p-6 shadow-lg border border-gray-600 hover:border-blue-500 transition-all duration-300"
                whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.5)" }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <div className="text-4xl text-blue-400 mb-4">{skill.icon}</div>
                <h3 className="text-xl font-bold mb-2">{skill.name}</h3>
                <p className="text-gray-300">{skill.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Tech Blogs Section */}
      <motion.section 
        className="py-20 bg-gray-900"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        transition={{ duration: 0.8 }}
      >
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">Tech Blogs</span>
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-8">
            {blogs.map((blog, index) => (
              <motion.div 
                key={index}
                className="bg-gray-800 rounded-xl overflow-hidden shadow-lg border border-gray-700 hover:border-blue-500 transition-all duration-300"
                whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.5)" }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <div className="p-6">
                  <div className="flex items-center mb-2">
                    <FaBookOpen className="text-blue-400 mr-2" />
                    <span className="text-sm text-gray-400">{blog.date}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">{blog.title}</h3>
                  <p className="text-gray-300 mb-4">{blog.excerpt}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {blog.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="bg-gray-700 text-xs px-3 py-1 rounded-full text-gray-300">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <a 
                    href={blog.link} 
                    className="text-blue-400 hover:text-blue-300 font-medium inline-flex items-center hover-effect"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Read More
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Contact Section */}
      <motion.section 
        className="py-20 bg-gray-800"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        transition={{ duration: 0.8 }}
      >
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">Get In Touch</span>
          </h2>
          
          <div className="max-w-3xl mx-auto bg-gray-700 rounded-2xl p-8 shadow-xl border border-gray-600">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex-1">
                <p className="text-lg text-gray-300 mb-6">
                  Interested in working together? Feel free to reach out through any of these channels:
                </p>
                
                <div className="space-y-4">
                  <a href="mailto:dhirajpraj@gmail.com" className="flex items-center text-gray-300 hover:text-blue-400 transition-colors duration-300">
                    <FaEnvelope className="mr-3 text-xl" />
                    <span>dhirajpraj@gmail.com</span>
                  </a>
                  
                  <a href="https://www.linkedin.com/in/dhiraj-prajapati-0482231b/" target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-300 hover:text-blue-400 transition-colors duration-300">
                    <FaLinkedin className="mr-3 text-xl" />
                    <span>Dhiraj Prajapati</span>
                  </a>
                </div>
              </div>
              
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center shadow-lg">
                <span className="text-4xl">DP</span>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Footer */}
      <footer className="py-8 bg-gray-800 border-t border-gray-700">
        <div className="container mx-auto px-6 text-center text-gray-500">
          <p>© {new Date().getFullYear()} Dhiraj Prajapati. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
